import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useGaContext } from "@/hooks/useGaContext";
import {
  Inbox, Download, FileText, ChevronDown, ChevronUp, Clock,
  CheckCircle2, Archive, AlertCircle, File, Plus, Trash2,
  MessageSquare, Send, ClipboardList, XCircle, Lock,
  Ban, X, FolderCheck, Users, ShieldCheck,
  ShieldAlert, ShieldX, Circle, TriangleAlert, RefreshCw,
  PackageOpen, FileDown, Briefcase, Search, SlidersHorizontal,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; icon: any; badge: string; dot: string }> = {
  new:          { label: "New",           icon: AlertCircle,   badge: "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300",    dot: "bg-blue-500" },
  in_review:    { label: "In Review",     icon: Clock,         badge: "bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300",  dot: "bg-amber-500" },
  pending_info: { label: "Pending Info",  icon: ClipboardList, badge: "bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300",dot: "bg-orange-500" },
  approved:     { label: "Approved",      icon: CheckCircle2,  badge: "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300",  dot: "bg-green-500" },
  declined:     { label: "Declined",      icon: XCircle,       badge: "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300",      dot: "bg-red-500" },
  closed:       { label: "Closed",        icon: Archive,       badge: "bg-muted text-muted-foreground",    dot: "bg-gray-400" },
};

const ITEM_CATEGORIES = ["SOH / APS", "Document", "Census", "Application", "Invoice", "Verification", "Other"];


function timeAgo(ts: string | null | undefined): string {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ─── Tab 1: Broker Requests ───────────────────────────────────────────────────
function PendingItemsPanel({ submission, gaId }: { submission: any; gaId: string }) {
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [form, setForm] = useState({ description: "", employeeName: "", category: "SOH / APS", dueDate: "" });

  const addMutation = useMutation({
    mutationFn: (data: any) =>
      apiRequest("POST", `/api/ga/${gaId}/submissions/${submission.id}/pending-items`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "submissions"] });
      setAdding(false);
      setForm({ description: "", employeeName: "", category: "SOH / APS", dueDate: "" });
      toast({ title: "Request added and visible to broker" });
    },
    onError: () => toast({ title: "Error", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: any }) =>
      apiRequest("PATCH", `/api/ga/${gaId}/submissions/${submission.id}/pending-items/${itemId}`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "submissions"] }),
    onError: () => toast({ title: "Error updating item", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) =>
      apiRequest("DELETE", `/api/ga/${gaId}/submissions/${submission.id}/pending-items/${itemId}`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "submissions"] }),
    onError: () => toast({ title: "Error", variant: "destructive" }),
  });

  const items: any[] = submission.pendingItems || [];
  const open     = items.filter((i: any) => i.status === "pending");
  const resolved = items.filter((i: any) => i.status !== "pending");

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-orange-500 dark:text-orange-400" />
          <span className="text-sm font-semibold text-foreground">Pending Items Requests</span>
          <span className="text-[10px] bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800 rounded px-1.5 py-0.5">Visible to broker</span>
          {open.length > 0 && (
            <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded-full px-2 py-0.5 font-medium">{open.length} open</span>
          )}
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setAdding(true)}>
          <Plus className="w-3 h-3" /> Request Item
        </Button>
      </div>

      {adding && (
        <div className="border border-orange-200 dark:border-orange-800 bg-orange-50/40 dark:bg-orange-950/40 rounded-lg p-3 space-y-3">
          <p className="text-xs text-orange-700 dark:text-orange-300 font-medium">This request will be visible to the broker on their case view.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">What are you requesting? *</Label>
              <Input
                placeholder="e.g. Statement of Health for John Smith"
                className="h-8 text-sm"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && form.description.trim() && addMutation.mutate(form)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Employee / Insured Name</Label>
              <Input placeholder="e.g. John Smith (optional)" className="h-8 text-sm"
                value={form.employeeName} onChange={(e) => setForm((f) => ({ ...f, employeeName: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{ITEM_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Due Date</Label>
              <Input type="date" className="h-8 text-sm" value={form.dueDate}
                onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" className="h-7 text-xs"
              disabled={!form.description.trim() || addMutation.isPending}
              onClick={() => addMutation.mutate(form)}>
              {addMutation.isPending ? "Sending…" : "Send Request"}
            </Button>
          </div>
        </div>
      )}

      {items.length === 0 && !adding ? (
        <p className="text-xs text-muted-foreground italic py-4">No requests sent — click "Request Item" to ask the broker for missing documents.</p>
      ) : (
        <div className="space-y-2">
          {open.map((item: any) => (
            <div key={item.id} className="rounded-lg border border-orange-100 dark:border-orange-800 bg-card px-3 py-2.5 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground">{item.description}</span>
                  {item.category && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{item.category}</Badge>}
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                  {item.employeeName && <span>👤 {item.employeeName}</span>}
                  {item.dueDate && <span className="text-orange-500 dark:text-orange-400">Due {item.dueDate}</span>}
                  {item.createdAt && <span>{timeAgo(item.createdAt)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost"
                  className="h-6 text-[11px] text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-950 px-2"
                  onClick={() => updateMutation.mutate({ itemId: item.id, data: { status: "received" } })}
                  disabled={updateMutation.isPending}>
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Received
                </Button>
                <Button size="sm" variant="ghost"
                  className="h-6 text-[11px] text-muted-foreground hover:text-muted-foreground px-2"
                  onClick={() => updateMutation.mutate({ itemId: item.id, data: { status: "waived" } })}
                  disabled={updateMutation.isPending} title="Mark as not required">
                  Not Required
                </Button>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground hover:text-red-400 dark:hover:text-red-500"
                  onClick={() => deleteMutation.mutate(item.id)} disabled={deleteMutation.isPending}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}

          {resolved.length > 0 && (
            <button className="text-xs text-muted-foreground hover:text-muted-foreground underline-offset-2 hover:underline pt-1"
              onClick={() => setShowResolved((v) => !v)}>
              {showResolved ? "Hide" : "Show"} {resolved.length} resolved item{resolved.length !== 1 ? "s" : ""}
            </button>
          )}
          {showResolved && resolved.map((item: any) => (
            <div key={item.id} className="rounded-lg border border-border bg-muted/60 px-3 py-2 flex items-start gap-3 opacity-60">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm line-through text-muted-foreground">{item.description}</span>
                  {item.status === "received" && <span className="text-[10px] bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded px-1.5 py-0.5 font-medium">Received</span>}
                  {item.status === "waived"   && <span className="text-[10px] bg-muted text-muted-foreground rounded px-1.5 py-0.5 font-medium">Not Required</span>}
                </div>
                {item.employeeName && <span className="text-xs text-muted-foreground">👤 {item.employeeName}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Tab 2: Messages ──────────────────────────────────────────────────────────
function MessageThread({ submission, gaId, senderRole, senderName }: {
  submission: any; gaId: string; senderRole: "ga" | "broker"; senderName: string;
}) {
  const { toast } = useToast();
  const [text, setText] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const messages: any[] = submission.messages || [];

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const sendMutation = useMutation({
    mutationFn: (msg: string) =>
      apiRequest("POST", `/api/ga/${gaId}/submissions/${submission.id}/messages`, { message: msg, senderName }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "submissions"] }); setText(""); },
    onError: () => toast({ title: "Failed to send", variant: "destructive" }),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-blue-500 dark:text-blue-400" />
        <span className="text-sm font-semibold text-foreground">Messages</span>
        <span className="text-[10px] bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800 rounded px-1.5 py-0.5">Visible to broker</span>
      </div>
      {messages.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No messages yet — use this thread to communicate with the broker.</p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {messages.map((msg: any) => {
            const isGA = msg.senderRole === "ga";
            return (
              <div key={msg.id} className={`flex ${isGA ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${isGA ? "bg-blue-600 text-white" : "bg-muted text-foreground"}`}>
                  <p className={`text-[10px] font-semibold mb-0.5 ${isGA ? "text-blue-200" : "text-muted-foreground"}`}>
                    {msg.senderName || (isGA ? "Underwriting" : "Broker")}
                  </p>
                  <p className="leading-snug">{msg.message}</p>
                  <p className={`text-[10px] mt-1 ${isGA ? "text-blue-300" : "text-muted-foreground"}`}>{timeAgo(msg.createdAt)}</p>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      )}
      <div className="flex gap-2">
        <Textarea placeholder="Send a message to the broker…" rows={2} className="text-sm resize-none"
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && text.trim()) { e.preventDefault(); sendMutation.mutate(text.trim()); } }} />
        <Button size="sm" className="self-end" disabled={!text.trim() || sendMutation.isPending}
          onClick={() => sendMutation.mutate(text.trim())}>
          {sendMutation.isPending
            ? <span className="animate-spin w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full" />
            : <Send className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
}

// ─── Document checklist defaults (fallback when no carrier config) ────────────
const DEFAULT_CHECKLIST_ITEMS = [
  { id: "emp-app",        label: "Employer Application (Sections 1 & 2)",           category: "Application"   },
  { id: "census",         label: "Signed Employee Census",                            category: "Census"        },
  { id: "carrier-enroll", label: "Signed Carrier Enrollment Forms (paper/wet sig)",  category: "Carrier Forms" },
  { id: "carrier-waiver", label: "Signed Waiver/Declination Forms (paper/wet sig)",  category: "Carrier Forms" },
  { id: "prior-bill",     label: "Prior Carrier Invoice (current month)",             category: "Invoice"       },
  { id: "de9c",           label: "DE9C / EDD Quarterly Wage Statement",              category: "Tax/Payroll"   },
  { id: "late-loi",       label: "Late Entrant Letter of Ineligibility",             category: "Document"      },
  { id: "cobra-docs",     label: "COBRA / Continuation Documentation",               category: "COBRA"         },
  { id: "rate-confirm",   label: "Rate / Contribution Confirmation",                 category: "Document"      },
];

type ChecklistItem = {
  id: string; label: string; category: string;
  required?: boolean; status: "pending" | "received" | "na";
  receivedAt?: string | null; notes?: string;
};

const STATUS_CYCLE: Array<ChecklistItem["status"]> = ["pending", "received", "na"];

function statusStyle(s: ChecklistItem["status"]) {
  if (s === "received") return { btn: "text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900", icon: <CheckCircle2 className="w-3 h-3" /> };
  if (s === "na")       return { btn: "text-muted-foreground bg-muted border-border hover:bg-muted/70",   icon: <Ban className="w-3 h-3" /> };
  return                       { btn: "text-orange-500 dark:text-orange-400 bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900", icon: <Circle className="w-3 h-3" /> };
}

const TIER_LABELS: Record<string, string> = {
  employee_only: "EE Only", employee: "EE Only",
  employee_spouse: "EE + Spouse", employee_children: "EE + Children",
  family: "Family", employee_domestic_partner: "EE + Dom. Partner",
};

const ENROLLMENT_STATUS_CONFIG: Record<string, { label: string; cls: string }> = {
  active:  { label: "Enrolled",  cls: "bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300" },
  pending: { label: "Pending",   cls: "bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300" },
  cobra:   { label: "COBRA",     cls: "bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300" },
  waived:  { label: "Waiving",   cls: "bg-muted text-muted-foreground" },
};

// ─── Per-carrier submission type (multi-carrier support) ──────────────────────
type CarrierEntry = {
  id: string;
  carrierId: string;
  carrierName: string;
  minPct: number;
  minCount: number;
  totalEligible: number;
  enrolling: number;
  waivingSpouse: number;
  waivingMedicare: number;
  waivingMilitary: number;
  waivingOther: number;
  cobra: number;
  ineligible: number;
  notes: string;
  checklist: ChecklistItem[];
};

function blankEntry(id?: string): CarrierEntry {
  return {
    id: id ?? `ce-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    carrierId: "", carrierName: "",
    minPct: 75, minCount: 2,
    totalEligible: 0, enrolling: 0,
    waivingSpouse: 0, waivingMedicare: 0, waivingMilitary: 0,
    waivingOther: 0, cobra: 0, ineligible: 0,
    notes: "",
    checklist: DEFAULT_CHECKLIST_ITEMS.map((d) => ({
      ...d, status: "pending" as const, required: true, receivedAt: null, notes: "",
    })),
  };
}

function initEntries(rawPart: any, rawChecklist: any): CarrierEntry[] {
  if (Array.isArray(rawPart?.carrierSubmissions) && rawPart.carrierSubmissions.length > 0) {
    return rawPart.carrierSubmissions as CarrierEntry[];
  }
  const legacyItems: ChecklistItem[] = rawChecklist?.items?.length > 0
    ? rawChecklist.items
    : DEFAULT_CHECKLIST_ITEMS.map((d) => ({
        ...d, status: "pending" as const, required: true, receivedAt: null, notes: "",
      }));
  return [{
    id: "legacy",
    carrierId: "",
    carrierName: rawPart?.carrierName || "",
    minPct: rawPart?.carrierMinPct ?? 75,
    minCount: rawPart?.carrierMinCount ?? 2,
    totalEligible: rawPart?.totalEligible ?? 0,
    enrolling: rawPart?.enrolling ?? 0,
    waivingSpouse: rawPart?.waivingSpouse ?? 0,
    waivingMedicare: rawPart?.waivingMedicare ?? 0,
    waivingMilitary: rawPart?.waivingMilitary ?? 0,
    waivingOther: rawPart?.waivingOther ?? 0,
    cobra: rawPart?.cobra ?? 0,
    ineligible: rawPart?.ineligible ?? 0,
    notes: rawPart?.notes ?? "",
    checklist: legacyItems,
  }];
}

// ─── Tab: UW Review ────────────────────────────────────────────────────────────
function UWReviewPanel({ submission, gaId }: {
  submission: any; gaId: string;
}) {
  const { toast } = useToast();
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType,   setFilterType]   = useState<string>("all");
  const [filterPlan,   setFilterPlan]   = useState<string>("all");
  const [entries, setEntries] = useState<CarrierEntry[]>(() =>
    initEntries(submission.participationData, submission.checklistData)
  );

  const { data: carrierConfigs = [] } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "carrier-configs"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/carrier-configs`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });
  const activeCarriers = (carrierConfigs as any[]).filter((c) => c.isActive);

  const { data: pkg, isLoading: pkgLoading } = useQuery<any>({
    queryKey: ["/api/ga", gaId, "submissions", submission.id, "enrollment-package"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/submissions/${submission.id}/enrollment-package`);
      if (!res.ok) return { enrollees: [], waivers: [], plans: [], clientName: null, summary: null };
      return res.json();
    },
    enabled: !!gaId && !!submission.id,
  });

  const enrollees: any[] = pkg?.enrollees || [];
  const waivers:   any[] = pkg?.waivers   || [];
  const missingDOB     = enrollees.filter((e) => !e.dob);
  const missingPremium = enrollees.filter((e) => e.employeePremium == null);
  const undocWaivers   = waivers.filter((w) => !w.waiverReason || w.waiverReason === "Not specified");
  const empIdCounts: Record<number, number> = {};
  enrollees.forEach((e) => { empIdCounts[e.employeeId] = (empIdCounts[e.employeeId] || 0) + 1; });
  const hasDupes = enrollees.some((e) => empIdCounts[e.employeeId] > 1);
  const validationIssues = [
    ...(missingDOB.length     ? [`${missingDOB.length} enrollee${missingDOB.length > 1 ? "s" : ""} missing date of birth`] : []),
    ...(missingPremium.length ? [`${missingPremium.length} enrollment${missingPremium.length > 1 ? "s" : ""} missing premium data`] : []),
    ...(undocWaivers.length   ? [`${undocWaivers.length} waiver${undocWaivers.length > 1 ? "s" : ""} without documented reason`] : []),
    ...(hasDupes              ? ["Duplicate enrollment records detected — review before submitting"] : []),
  ];
  const hasData = enrollees.length > 0 || waivers.length > 0;

  const saveMutation = useMutation({
    mutationFn: (data: any) => apiRequest("PATCH", `/api/ga/${gaId}/submissions/${submission.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "submissions"] });
      setSavedAt(new Date().toLocaleTimeString());
      toast({ title: "Saved" });
    },
    onError: () => toast({ title: "Error saving", variant: "destructive" }),
  });

  const save = () => saveMutation.mutate({ participationData: { carrierSubmissions: entries } });

  const updateEntry = (id: string, patch: Partial<CarrierEntry>) =>
    setEntries((prev) => prev.map((e) => e.id === id ? { ...e, ...patch } : e));

  const selectCarrier = (entryId: string, configId: string) => {
    const config = activeCarriers.find((c) => String(c.id) === configId);
    if (!config) return;
    const newChecklist: ChecklistItem[] = (config.checklistItems || []).length > 0
      ? (config.checklistItems as any[]).map((item: any, i: number) => ({
          id: item.id || `ci-${i}-${Date.now()}`,
          label: item.label || item.name || "",
          category: item.category || "Document",
          required: item.required ?? true,
          status: "pending" as const,
          receivedAt: null,
          notes: item.notes || "",
        }))
      : DEFAULT_CHECKLIST_ITEMS.map((d) => ({
          ...d, status: "pending" as const, required: true, receivedAt: null, notes: "",
        }));
    const patch = {
      carrierId: configId,
      carrierName: config.displayName || config.carrierName,
      minPct: config.minParticipationPct ?? 75,
      minCount: config.minLives ?? 2,
      checklist: newChecklist,
    };
    // Update local state AND immediately persist so closing the accordion doesn't lose the selection
    setEntries((prev) => {
      const updated = prev.map((e) => e.id === entryId ? { ...e, ...patch } : e);
      saveMutation.mutate({ participationData: { carrierSubmissions: updated } });
      return updated;
    });
    toast({ title: `Loaded ${config.displayName || config.carrierName} requirements` });
  };

  const cycleItem = (entryId: string, itemId: string) =>
    setEntries((prev) => prev.map((e) => {
      if (e.id !== entryId) return e;
      return {
        ...e, checklist: e.checklist.map((item) => {
          if (item.id !== itemId) return item;
          const cur  = STATUS_CYCLE.indexOf(item.status);
          const next = STATUS_CYCLE[(cur + 1) % STATUS_CYCLE.length];
          return { ...item, status: next, receivedAt: next === "received" ? new Date().toISOString().slice(0, 10) : item.receivedAt };
        }),
      };
    }));

  const addEntry  = () => setEntries((prev) => [...prev, blankEntry()]);
  const removeEntry = (id: string) => setEntries((prev) => prev.filter((e) => e.id !== id));

  const downloadCSV = async (type: "all" | "enrollees" | "waivers") => {
    try {
      const res = await fetch(`/api/ga/${gaId}/submissions/${submission.id}/enrollment-package/csv?type=${type}`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `enrollment-${type}-${submission.id.slice(0, 8)}.csv`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast({ title: "Download failed", variant: "destructive" }); }
  };

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <p className="text-xs text-muted-foreground">Internal workbench — not visible to broker or employer.</p>
        </div>
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 text-muted-foreground border-border cursor-not-allowed" disabled>
          <PackageOpen className="w-3.5 h-3.5" /> Download All Files
        </Button>
      </div>

      {/* ══ SECTION 1: Per-Carrier Participation + Document Checklist ══════════ */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-semibold text-foreground">Participation & Document Checklist</span>
            <span className="text-[10px] bg-muted text-muted-foreground border border-border rounded px-1.5 py-0.5">per carrier</span>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addEntry}>
            <Plus className="w-3 h-3" /> Add Carrier
          </Button>
        </div>

        {entries.map((entry) => {
          const validWaivers    = entry.waivingSpouse + entry.waivingMedicare + entry.waivingMilitary;
          const allAccountedFor = entry.enrolling + validWaivers + entry.waivingOther + entry.cobra + entry.ineligible;
          const netEligible     = Math.max(0, entry.totalEligible - validWaivers - entry.ineligible);
          const partRate        = netEligible > 0 ? Math.round((entry.enrolling / netEligible) * 100) : 0;
          const passes          = entry.enrolling >= entry.minCount && partRate >= entry.minPct && entry.totalEligible > 0;
          const warn            = !passes && entry.totalEligible > 0 && (partRate >= entry.minPct - 5 || entry.enrolling >= entry.minCount);
          const mismatch        = entry.totalEligible > 0 && Math.abs(allAccountedFor - entry.totalEligible) > 0;
          const partColor       = passes ? "text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800"
            : warn ? "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800"
            : entry.totalEligible === 0 ? "text-muted-foreground bg-muted border-border"
            : "text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800";
          const PartIcon = passes ? ShieldCheck : warn ? ShieldAlert : entry.totalEligible === 0 ? ShieldAlert : ShieldX;
          const received = entry.checklist.filter((i) => i.status === "received").length;
          const pending  = entry.checklist.filter((i) => i.status === "pending").length;

          return (
            <div key={entry.id} className="rounded-lg border border-border overflow-hidden">
              {/* Card header: carrier selector */}
              <div className="flex items-center gap-3 px-4 py-2.5 bg-muted border-b border-border flex-wrap">
                <Select
                  value={entry.carrierId || "__select__"}
                  onValueChange={(v) => v !== "__select__" && selectCarrier(entry.id, v)}
                >
                  <SelectTrigger className="h-7 text-xs w-56 bg-card shrink-0">
                    <SelectValue placeholder="Select carrier from settings…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__select__" disabled>Select carrier from settings…</SelectItem>
                    {activeCarriers.length === 0 && (
                      <SelectItem value="__none__" disabled>No carriers yet — add in Carrier Settings</SelectItem>
                    )}
                    {activeCarriers.map((c: any) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.displayName || c.carrierName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!entry.carrierId && (
                  <input
                    type="text"
                    placeholder="Or type carrier name manually…"
                    className="h-7 text-xs border border-border rounded px-2 flex-1 min-w-0 focus:outline-none focus:border-blue-300 bg-card"
                    value={entry.carrierName}
                    onChange={(e) => updateEntry(entry.id, { carrierName: e.target.value })}
                  />
                )}
                {entry.carrierId && (
                  <span className="text-sm font-medium text-foreground flex-1 truncate">{entry.carrierName}</span>
                )}
                {pkg?.summary && (
                  <Button size="sm" variant="ghost"
                    className="h-6 text-[10px] text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950 gap-1 shrink-0"
                    onClick={() => updateEntry(entry.id, {
                      enrolling:     pkg.summary.totalEnrolling,
                      totalEligible: Math.max(entry.totalEligible, pkg.summary.totalEligible),
                    })}>
                    <RefreshCw className="w-3 h-3" /> Import counts
                  </Button>
                )}
                {entries.length > 1 && (
                  <Button size="sm" variant="ghost"
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-red-400 dark:hover:text-red-500 shrink-0"
                    onClick={() => removeEntry(entry.id)}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>

              {/* Two-column: Participation LEFT, Checklist RIGHT */}
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_290px] divide-y lg:divide-y-0 lg:divide-x divide-border">

                {/* LEFT: Participation */}
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {([
                      ["totalEligible", "Total Eligible"],
                      ["enrolling",     "Enrolling"],
                      ["ineligible",    "Ineligible"],
                      ["cobra",         "COBRA"],
                    ] as [keyof CarrierEntry, string][]).map(([k, label]) => (
                      <div key={k} className="space-y-0.5">
                        <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide block">{label}</label>
                        <input type="number" min={0} value={entry[k] as number}
                          onChange={(e) => updateEntry(entry.id, { [k]: parseInt(e.target.value) || 0 })}
                          className="w-full h-8 border border-border rounded px-2 text-sm focus:outline-none focus:border-blue-300" />
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Valid Waivers — excluded from denominator</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {([
                        ["waivingSpouse",   "Spouse / DP"],
                        ["waivingMedicare", "Medicare"],
                        ["waivingMilitary", "TRICARE"],
                        ["waivingOther",    "Other ⚠"],
                      ] as [keyof CarrierEntry, string][]).map(([k, label]) => (
                        <div key={k} className="space-y-0.5">
                          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide block">{label}</label>
                          <input type="number" min={0} value={entry[k] as number}
                            onChange={(e) => updateEntry(entry.id, { [k]: parseInt(e.target.value) || 0 })}
                            className={`w-full h-8 border rounded px-2 text-sm focus:outline-none focus:border-blue-300 ${
                              k === "waivingOther" && entry.waivingOther > 0 ? "border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950" : "border-border"
                            }`} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-0.5">
                      <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide block">Min Participation %</label>
                      <input type="number" min={0} max={100} value={entry.minPct}
                        onChange={(e) => updateEntry(entry.id, { minPct: parseInt(e.target.value) || 0 })}
                        className="w-full h-8 border border-border rounded px-2 text-sm focus:outline-none focus:border-blue-300" />
                    </div>
                    <div className="space-y-0.5">
                      <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide block">Min Enrolled Lives</label>
                      <input type="number" min={0} value={entry.minCount}
                        onChange={(e) => updateEntry(entry.id, { minCount: parseInt(e.target.value) || 0 })}
                        className="w-full h-8 border border-border rounded px-2 text-sm focus:outline-none focus:border-blue-300" />
                    </div>
                  </div>
                  {mismatch && (
                    <div className="flex items-start gap-2 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-2 py-1.5">
                      <TriangleAlert className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        Numbers don't add up: {allAccountedFor} accounted for vs. {entry.totalEligible} total eligible (diff: {Math.abs(allAccountedFor - entry.totalEligible)}).
                      </p>
                    </div>
                  )}
                  <div className={`rounded-lg border p-3 ${partColor}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <PartIcon className="w-4 h-4 shrink-0" />
                        <span className="text-sm font-semibold">
                          {entry.totalEligible === 0 ? "Enter headcount to calculate"
                            : passes ? "Participation Met"
                            : warn   ? "Borderline — Verify with Carrier"
                            : "Participation Not Met"}
                        </span>
                      </div>
                      {entry.totalEligible > 0 && <span className="text-lg font-bold">{partRate}%</span>}
                    </div>
                    {entry.totalEligible > 0 && (
                      <p className="text-xs mt-0.5 opacity-75">
                        {entry.enrolling} enrolled / {netEligible} net eligible — requires {entry.minPct}% and ≥{entry.minCount} lives
                      </p>
                    )}
                  </div>
                  <textarea rows={2} value={entry.notes}
                    onChange={(e) => updateEntry(entry.id, { notes: e.target.value })}
                    placeholder="Participation notes, exceptions, carrier conversations…"
                    className="w-full border border-border rounded px-2 py-1.5 text-xs resize-none focus:outline-none focus:border-blue-300 text-foreground" />
                </div>

                {/* RIGHT: Document Checklist */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                      <FolderCheck className="w-3.5 h-3.5 text-muted-foreground" /> Documents
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      <span className="text-green-600 dark:text-green-400 font-medium">{received}</span>/{entry.checklist.length} received
                      {pending > 0 && <span className="text-orange-500 dark:text-orange-400 font-medium ml-1">· {pending} pending</span>}
                    </span>
                  </div>
                  {entry.checklist.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <FolderCheck className="w-6 h-6 mx-auto mb-1.5 opacity-30" />
                      <p className="text-[11px]">
                        {entry.carrierId
                          ? "No documents configured in Carrier Settings for this carrier."
                          : "Select a carrier above to load its document checklist."}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded border border-border divide-y divide-border overflow-y-auto" style={{ maxHeight: 340 }}>
                      {entry.checklist.map((item) => {
                        const { btn, icon } = statusStyle(item.status);
                        return (
                          <div key={item.id}
                            className={`flex items-center gap-2 px-2.5 py-1.5 hover:bg-muted/60 ${item.status === "na" ? "opacity-40" : ""}`}>
                            <button
                              onClick={() => cycleItem(entry.id, item.id)}
                              title="Pending → Received → N/A"
                              className={`w-5 h-5 flex items-center justify-center rounded border shrink-0 transition-colors ${btn}`}>
                              {icon}
                            </button>
                            <span className={`text-[11px] leading-tight flex-1 min-w-0 ${item.status === "na" ? "line-through text-muted-foreground" : "text-foreground"}`}>
                              {item.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {!entry.carrierId && activeCarriers.length > 0 && (
                    <p className="text-[10px] text-blue-500 dark:text-blue-400 mt-2">↑ Select a carrier to auto-load its document checklist.</p>
                  )}
                  {!entry.carrierId && activeCarriers.length === 0 && (
                    <p className="text-[10px] text-muted-foreground mt-2">Add carriers in Carrier Settings to enable auto-loading.</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ══ SECTION 2: Carrier Package (live enrollment data) ══════════════════ */}
      <div className="border-t pt-4">
        {/* Header row */}
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-semibold text-foreground">Carrier Package</span>
            {pkg?.clientName && <span className="text-xs text-muted-foreground">— {pkg.clientName}</span>}
            <span className="text-[10px] bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800 rounded px-1.5 py-0.5">Live from enrollment portal</span>
          </div>
          {hasData && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadCSV("enrollees")}>
                <Download className="w-3 h-3 mr-1" /> Enrollment Roster
              </Button>
              {waivers.length > 0 && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadCSV("waivers")}>
                  <Download className="w-3 h-3 mr-1" /> Waivers
                </Button>
              )}
              <Button size="sm" className="h-7 text-xs" onClick={() => downloadCSV("all")}>
                <Download className="w-3 h-3 mr-1" /> Download All (CSV)
              </Button>
            </div>
          )}
        </div>

        {/* ── Prominent stats strip ── */}
        {(hasData || pkgLoading) && (() => {
          // Canonical map: any variation → canonical label
          const CANONICAL: Record<string, string> = {
            "full-time": "Full-Time", "fulltime": "Full-Time", "full time": "Full-Time",
            "part-time": "Part-Time", "parttime": "Part-Time", "part time": "Part-Time",
            "variable-hour": "Variable-Hour", "variable hour": "Variable-Hour",
            "seasonal": "Seasonal",
            "contractor": "Contractor",
            "intern": "Intern",
          };
          const canonicalize = (t: string | null): string => {
            if (!t) return "Unknown";
            return CANONICAL[t.trim().toLowerCase()] ?? t.trim().replace(/\b\w/g, (c) => c.toUpperCase());
          };
          const typeCounts: Record<string, number> = {};
          enrollees.forEach((e: any) => {
            const t = canonicalize(e.employmentType);
            typeCounts[t] = (typeCounts[t] || 0) + 1;
          });
          const cobraCount = enrollees.filter((e: any) => e.enrollmentStatus === "cobra").length;

          // Always-present type boxes (show 0 when none)
          const FIXED_TYPES: { key: string; color: string }[] = [
            { key: "Full-Time", color: "bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300" },
            { key: "Part-Time", color: "bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300" },
          ];
          const EXTRA_TYPE_COLORS: Record<string, string> = {
            "Variable-Hour": "bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300",
            "Seasonal":      "bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300",
            "Contractor":    "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300",
            "Intern":        "bg-pink-50 dark:bg-pink-950 border-pink-200 dark:border-pink-800 text-pink-700 dark:text-pink-300",
            "Unknown":       "bg-muted border-border text-muted-foreground",
          };
          const fixedKeys = new Set(FIXED_TYPES.map((f) => f.key));
          const extraEntries = Object.entries(typeCounts)
            .filter(([t]) => !fixedKeys.has(t))
            .sort((a, b) => b[1] - a[1]);

          const totalCols = 3 + FIXED_TYPES.length + 1 + extraEntries.length;
          return (
            <div className="grid gap-2 mb-4" style={{ gridTemplateColumns: `repeat(${totalCols}, 1fr)` }}>
              {/* Summary boxes */}
              <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 px-3 py-2.5">
                <p className="text-[10px] font-medium text-blue-500 dark:text-blue-400 uppercase tracking-wide">Enrolling</p>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 leading-tight">{pkgLoading ? "—" : enrollees.length}</p>
                <p className="text-[10px] text-blue-400 dark:text-blue-500 mt-0.5">active elections</p>
              </div>
              <div className="rounded-lg border border-border bg-muted px-3 py-2.5">
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Waiving</p>
                <p className="text-2xl font-bold text-foreground leading-tight">{pkgLoading ? "—" : waivers.length}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {undocWaivers.length > 0
                    ? <span className="text-amber-500 dark:text-amber-400">{undocWaivers.length} undocumented</span>
                    : "all documented"}
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5">
                <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Eligible</p>
                <p className="text-2xl font-bold text-slate-700 dark:text-slate-300 leading-tight">{pkgLoading ? "—" : (enrollees.length + waivers.length)}</p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">enrollees + waivers</p>
              </div>
              {/* Always-visible type boxes */}
              {FIXED_TYPES.map(({ key, color }) => (
                <div key={key} className={`rounded-lg border px-3 py-2.5 ${color}`}>
                  <p className="text-[10px] font-medium uppercase tracking-wide opacity-70">{key}</p>
                  <p className="text-2xl font-bold leading-tight">{pkgLoading ? "—" : (typeCounts[key] ?? 0)}</p>
                  <p className="text-[10px] opacity-60 mt-0.5">employees</p>
                </div>
              ))}
              {/* Always-visible COBRA box */}
              <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950 px-3 py-2.5">
                <p className="text-[10px] font-medium text-purple-500 dark:text-purple-400 uppercase tracking-wide">COBRA</p>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-300 leading-tight">{pkgLoading ? "—" : cobraCount}</p>
                <p className="text-[10px] text-purple-400 dark:text-purple-500 mt-0.5">continuation</p>
              </div>
              {/* Any extra types (Variable-Hour, Seasonal, etc.) only when they have data */}
              {extraEntries.map(([type, count]) => (
                <div key={type} className={`rounded-lg border px-3 py-2.5 ${EXTRA_TYPE_COLORS[type] || "bg-muted border-border text-muted-foreground"}`}>
                  <p className="text-[10px] font-medium uppercase tracking-wide opacity-70">{type}</p>
                  <p className="text-2xl font-bold leading-tight">{count}</p>
                  <p className="text-[10px] opacity-60 mt-0.5">employees</p>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Validation alerts */}
        {validationIssues.length > 0 && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-3 py-2.5 mb-3 space-y-1">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1">
              <TriangleAlert className="w-3.5 h-3.5" /> Issues — Resolve Before Carrier Submission
            </p>
            {validationIssues.map((issue, i) => <p key={i} className="text-xs text-amber-600 dark:text-amber-400 ml-5">• {issue}</p>)}
          </div>
        )}

        {pkgLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : !enrollees.length ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">{pkg?.clientName === null ? "No client linked to this submission" : "No enrollment data for this group yet"}</p>
            <p className="text-xs mt-1">Data will populate once employees complete enrollment</p>
          </div>
        ) : (() => {
          const CANONICAL_F: Record<string, string> = {
            "full-time": "Full-Time", "fulltime": "Full-Time", "full time": "Full-Time",
            "part-time": "Part-Time", "parttime": "Part-Time", "part time": "Part-Time",
            "variable-hour": "Variable-Hour", "variable hour": "Variable-Hour",
            "seasonal": "Seasonal", "contractor": "Contractor", "intern": "Intern",
          };
          const normType = (t: string | null): string => {
            if (!t) return "Unknown";
            return CANONICAL_F[t.trim().toLowerCase()] ?? t.trim().replace(/\b\w/g, (c) => c.toUpperCase());
          };
          // Derive filter options from data
          const planOptions   = Array.from(new Set(enrollees.map((e: any) => e.planName).filter(Boolean))) as string[];
          const typeOptions   = Array.from(new Set(enrollees.map((e: any) => normType(e.employmentType)).filter((t) => t !== "Unknown"))) as string[];
          const statusOptions = Array.from(new Set(enrollees.map((e: any) => e.enrollmentStatus).filter(Boolean))) as string[];

          const filtered = enrollees.filter((e: any) => {
            const q = filterSearch.toLowerCase();
            if (q && !`${e.firstName} ${e.lastName} ${e.email}`.toLowerCase().includes(q)) return false;
            if (filterStatus !== "all" && e.enrollmentStatus !== filterStatus) return false;
            if (filterType   !== "all" && normType(e.employmentType) !== filterType) return false;
            if (filterPlan   !== "all" && e.planName         !== filterPlan)   return false;
            return true;
          });

          const anyFilter = filterSearch || filterStatus !== "all" || filterType !== "all" || filterPlan !== "all";

          return (
            <>
              {/* Filter bar */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <SlidersHorizontal className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <div className="relative flex-1 min-w-[160px] max-w-[220px]">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search employee…"
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    className="w-full pl-6 pr-2 py-1.5 text-xs border border-border rounded-md bg-card focus:outline-none focus:ring-1 focus:ring-blue-300"
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="h-7 text-xs w-[120px]"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {statusOptions.map((s) => (
                      <SelectItem key={s} value={s}>
                        {ENROLLMENT_STATUS_CONFIG[s]?.label ?? s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="h-7 text-xs w-[130px]"><SelectValue placeholder="Emp. Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {typeOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterPlan} onValueChange={setFilterPlan}>
                  <SelectTrigger className="h-7 text-xs w-[160px]"><SelectValue placeholder="Plan" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    {planOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                {anyFilter && (
                  <button
                    onClick={() => { setFilterSearch(""); setFilterStatus("all"); setFilterType("all"); setFilterPlan("all"); }}
                    className="text-xs text-muted-foreground hover:text-muted-foreground underline"
                  >
                    Clear
                  </button>
                )}
                <span className="text-[10px] text-muted-foreground ml-auto">
                  {filtered.length} of {enrollees.length} shown
                </span>
              </div>

              {/* Table */}
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Employee</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Type</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Plan</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Tier</th>
                      <th className="px-3 py-2 text-center font-medium text-muted-foreground">Form</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.length === 0 ? (
                      <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground text-xs">No employees match the current filters.</td></tr>
                    ) : filtered.map((e: any) => {
                      const isDupe = empIdCounts[e.employeeId] > 1;
                      const flag   = isDupe || !e.dob || e.employeePremium == null;
                      const statusInfo = ENROLLMENT_STATUS_CONFIG[e.enrollmentStatus as keyof typeof ENROLLMENT_STATUS_CONFIG]
                        ?? { label: e.enrollmentStatus ?? "Unknown", cls: "bg-muted text-muted-foreground" };
                      return (
                        <tr key={e.enrollmentId} className={`hover:bg-muted/50 ${isDupe ? "bg-amber-50/40 dark:bg-amber-950/40" : ""}`}>
                          <td className="px-3 py-2 font-medium text-foreground">
                            <div className="flex items-center gap-1">
                              {flag && (
                                <span title="Has issues" className="shrink-0 inline-flex">
                                  <TriangleAlert className="w-3 h-3 text-amber-400 dark:text-amber-500" />
                                </span>
                              )}
                              {e.lastName}, {e.firstName}
                            </div>
                            <div className="text-[10px] text-muted-foreground">{e.email}</div>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {e.employmentType ? (
                              <span className="inline-flex items-center gap-1">
                                <Briefcase className="w-3 h-3 text-muted-foreground" />
                                {e.employmentType}
                              </span>
                            ) : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`inline-block rounded px-1.5 py-0.5 font-medium ${statusInfo.cls}`}>
                              {statusInfo.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground max-w-[160px]">
                            <span className="truncate block">{e.planName || "—"}</span>
                          </td>
                          <td className="px-3 py-2">
                            {e.coverageTier ? (
                              <span className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 rounded px-1.5 py-0.5">
                                {TIER_LABELS[e.coverageTier] || e.coverageTier}
                              </span>
                            ) : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              title="Download application form"
                              className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-blue-50 dark:hover:bg-blue-950 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                              onClick={() => toast({ title: "Form download", description: "Application forms are generated by the enrollment system." })}
                            >
                              <FileDown className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          );
        })()}
      </div>

      {/* ── Save footer ── */}
      <div className="flex items-center justify-between pt-1 border-t">
        <span className="text-xs text-muted-foreground">{savedAt ? `Last saved at ${savedAt}` : "Unsaved changes"}</span>
        <Button size="sm" className="h-8 text-xs" onClick={save} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? "Saving…" : "Save Participation & Checklists"}
        </Button>
      </div>
    </div>
  );
}

// ─── Tab 4: UW Notes (Internal) ───────────────────────────────────────────────
function UWNotesPanel({ submission, gaId }: { submission: any; gaId: string }) {
  const { toast } = useToast();
  const [notes, setNotes]         = useState(submission.uwNotes || "");
  const [assignedTo, setAssignedTo] = useState(submission.assignedTo || "");
  const [dirty, setDirty]         = useState(false);
  const [savedAt, setSavedAt]     = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: () => apiRequest("PATCH", `/api/ga/${gaId}/submissions/${submission.id}`, { uwNotes: notes, assignedTo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "submissions"] });
      setDirty(false); setSavedAt(new Date().toLocaleTimeString());
      toast({ title: "Notes saved" });
    },
    onError: () => toast({ title: "Error saving", variant: "destructive" }),
  });

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-100 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/40 px-3 py-2 flex items-center gap-2">
        <Lock className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 shrink-0" />
        <p className="text-xs text-amber-700 dark:text-amber-300"><span className="font-medium">Internal only</span> — never visible to the broker or employer.</p>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Assigned Underwriter</Label>
        <Input placeholder="e.g. Sarah Chen" className="h-8 text-sm" value={assignedTo}
          onChange={(e) => { setAssignedTo(e.target.value); setDirty(true); }} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Internal UW Notes</Label>
        <Textarea placeholder="Risk observations, decision rationale, flags for review, referral notes…" rows={6}
          className="text-sm resize-none" value={notes}
          onChange={(e) => { setNotes(e.target.value); setDirty(true); }} />
      </div>
      <div className="flex items-center justify-between">
        {savedAt ? <span className="text-xs text-muted-foreground">Last saved at {savedAt}</span> : <span />}
        <Button size="sm" className="h-7 text-xs" disabled={!dirty || saveMutation.isPending} onClick={() => saveMutation.mutate()}>
          {saveMutation.isPending ? "Saving…" : "Save Notes"}
        </Button>
      </div>
    </div>
  );
}

// ─── Submission Row ───────────────────────────────────────────────────────────
function SubmissionRow({ submission, gaId }: { submission: any; gaId: string }) {
  const [expanded, setExpanded]   = useState(false);
  const [activeTab, setActiveTab] = useState<"items" | "messages" | "review" | "uw">("review");
  const { toast } = useToast();
  const cfg = STATUS_CONFIG[submission.status] || STATUS_CONFIG.new;

  const openRequests = (submission.pendingItems || []).filter((i: any) => i.status === "pending").length;
  const msgCount     = (submission.messages || []).length;

  const updateMutation = useMutation({
    mutationFn: (data: any) => apiRequest("PATCH", `/api/ga/${gaId}/submissions/${submission.id}`, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "submissions"] }); toast({ title: "Updated" }); },
    onError: () => toast({ title: "Error", variant: "destructive" }),
  });

  const downloadFile = async (fileId: string, fileName: string) => {
    try {
      const res = await fetch(`/api/ga/${gaId}/submissions/${submission.id}/files/${fileId}/download`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast({ title: "Download failed", variant: "destructive" }); }
  };

  const tabs = [
    { id: "review" as const, label: "UW Review", icon: <FolderCheck className="w-3 h-3" />, badge: null },
    {
      id: "items" as const, label: "Pending Items Requests", icon: <ClipboardList className="w-3 h-3" />,
      badge: openRequests > 0 ? <span className="ml-1 bg-orange-500 text-white rounded-full text-[9px] px-1.5 py-0 leading-4">{openRequests}</span> : null,
    },
    {
      id: "messages" as const, label: "Messages", icon: <MessageSquare className="w-3 h-3" />,
      badge: msgCount > 0 ? <span className="ml-1 bg-blue-500 text-white rounded-full text-[9px] px-1.5 py-0 leading-4">{msgCount}</span> : null,
    },
    { id: "uw" as const, label: "UW Notes", icon: <Lock className="w-3 h-3" />, badge: null },
  ];

  return (
    <div className={`border rounded-lg overflow-hidden transition-shadow ${expanded ? "shadow-sm border-blue-200 dark:border-blue-800" : "border-border"}`}>
      <div className="w-full text-left px-4 py-3 bg-card hover:bg-muted transition-colors cursor-pointer"
        onClick={() => setExpanded((v) => !v)}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium shrink-0 ${cfg.badge}`}>
              <cfg.icon className="w-3 h-3" /> {cfg.label}
            </span>
            <div className="min-w-0">
              <p className="font-medium text-foreground truncate">{submission.subject}</p>
              <p className="text-xs text-muted-foreground">
                {submission.brokerAgencyName || submission.brokerAccountId} ·{" "}
                {new Date(submission.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                {openRequests > 0 && <span className="ml-2 text-orange-500 dark:text-orange-400 font-medium">{openRequests} open request{openRequests !== 1 ? "s" : ""}</span>}
                {msgCount     > 0 && <span className="ml-2 text-blue-400 dark:text-blue-500">{msgCount} msg{msgCount !== 1 ? "s" : ""}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Select value={submission.status} onValueChange={(v) => updateMutation.mutate({ status: v })}>
              <SelectTrigger className="h-7 text-xs w-36" onClick={(e) => e.stopPropagation()}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent onClick={(e) => e.stopPropagation()}>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_review">In Review</SelectItem>
                <SelectItem value="pending_info">Pending Info</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border">
          <div className="flex border-b border-border bg-muted overflow-x-auto">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors whitespace-nowrap flex items-center gap-1 ${
                  activeTab === tab.id ? "border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-card" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}>
                {tab.icon}{tab.label}{tab.badge}
              </button>
            ))}
          </div>
          <div className="p-4 bg-card">
            {activeTab === "items"    && <PendingItemsPanel submission={submission} gaId={gaId} />}
            {activeTab === "messages" && <MessageThread submission={submission} gaId={gaId} senderRole="ga" senderName="Underwriting Team" />}
            {activeTab === "review"   && <UWReviewPanel submission={submission} gaId={gaId} />}
            {activeTab === "uw"       && <UWNotesPanel submission={submission} gaId={gaId} />}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function GASubmissions() {
  const { gaId } = useGaContext();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: submissions = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "submissions"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/submissions`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const filtered = statusFilter === "all" ? submissions : submissions.filter((s: any) => s.status === statusFilter);

  const counts: Record<string, number> = {
    all:          submissions.length,
    new:          submissions.filter((s: any) => s.status === "new").length,
    in_review:    submissions.filter((s: any) => s.status === "in_review").length,
    pending_info: submissions.filter((s: any) => s.status === "pending_info").length,
    approved:     submissions.filter((s: any) => s.status === "approved").length,
    declined:     submissions.filter((s: any) => s.status === "declined").length,
    closed:       submissions.filter((s: any) => s.status === "closed").length,
  };

  const totalOpenRequests = submissions.reduce(
    (acc: number, s: any) => acc + (s.pendingItems || []).filter((i: any) => i.status === "pending").length, 0
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Case Submissions</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Underwriting workbench — track cases, request info, and communicate with brokers</p>
        </div>
        {totalOpenRequests > 0 && (
          <div className="flex items-center gap-2 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg px-3 py-2">
            <ClipboardList className="w-4 h-4 text-orange-500 dark:text-orange-400" />
            <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
              {totalOpenRequests} open broker request{totalOpenRequests !== 1 ? "s" : ""} across all cases
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {(["new", "in_review", "pending_info", "approved", "declined", "closed"] as const).map((s) => {
          const cfg = STATUS_CONFIG[s];
          return (
            <button key={s} onClick={() => setStatusFilter(s === statusFilter ? "all" : s)}
              className={`rounded-xl border p-3 text-left transition-all ${
                statusFilter === s ? "border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-950 shadow-sm" : "border-border bg-card hover:border-muted-foreground/30"
              }`}>
              <div className={`w-2 h-2 rounded-full mb-2 ${cfg.dot}`} />
              <p className="text-xl font-bold text-foreground">{counts[s]}</p>
              <p className="text-xs text-muted-foreground">{cfg.label}</p>
            </button>
          );
        })}
      </div>

      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {(["all", "new", "in_review", "pending_info", "approved", "declined", "closed"] as const).map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              statusFilter === s ? "border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}>
            {s === "all" ? "All" : STATUS_CONFIG[s].label}
            <span className="ml-1.5 bg-muted text-muted-foreground rounded-full px-1.5 py-0.5">{counts[s]}</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16 text-muted-foreground">
            <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">
              {statusFilter === "all" ? "No case submissions yet" : `No ${STATUS_CONFIG[statusFilter]?.label || statusFilter} submissions`}
            </p>
            <p className="text-xs mt-1 text-muted-foreground">Submissions from your broker partners will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((submission: any) => (
            <SubmissionRow key={submission.id} submission={submission} gaId={gaId || ""} />
          ))}
        </div>
      )}
    </div>
  );
}
